import { useState, useEffect, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { authHeaders } from '../utils/auth';
import { apiFetch } from '../utils/api';
import type { ApiErrorBody } from '../types';

export default function ChangePassword() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!localStorage.getItem('token')) {
      navigate('/login', { replace: true });
    }
  }, [navigate]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (form.newPassword !== form.confirmPassword) {
      setError('New passwords do not match.');
      return;
    }
    if (form.newPassword.length < 8) {
      setError('New password must be at least 8 characters.');
      return;
    }
    if (form.newPassword === form.currentPassword) {
      setError('New password must be different from your current password.');
      return;
    }

    setLoading(true);
    try {
      const res = await apiFetch('/api/auth/change-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({
          currentPassword: form.currentPassword,
          newPassword: form.newPassword,
        }),
      });
      const data: ApiErrorBody = await res.json();
      if (!res.ok) {
        setError(data.message || 'Failed to change password.');
        return;
      }
      setSuccess(true);
      setForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch {
      setError('Network error — please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ maxWidth: 480, margin: '0 auto' }}>
      <h2 style={{ color: 'var(--color-primary)', marginBottom: 24 }}>My Account</h2>

      {success && (
        <div style={{
          background: '#d1fae5',
          border: '1px solid #6ee7b7',
          borderRadius: 6,
          padding: '12px 16px',
          marginBottom: 24,
          color: '#065f46',
          fontWeight: 500,
        }}>
          ✓ Password changed successfully.
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className='user-form-field'>
          <label>Current Password</label>
          <input
            type='password'
            value={form.currentPassword}
            onChange={e => setForm(f => ({ ...f, currentPassword: e.target.value }))}
            required
            autoComplete='current-password'
          />
        </div>
        <div className='user-form-field' style={{ marginTop: 16 }}>
          <label>New Password</label>
          <input
            type='password'
            value={form.newPassword}
            onChange={e => setForm(f => ({ ...f, newPassword: e.target.value }))}
            required
            minLength={8}
            autoComplete='new-password'
          />
        </div>
        <div className='user-form-field' style={{ marginTop: 16 }}>
          <label>Confirm New Password</label>
          <input
            type='password'
            value={form.confirmPassword}
            onChange={e => setForm(f => ({ ...f, confirmPassword: e.target.value }))}
            required
            minLength={8}
            autoComplete='new-password'
          />
        </div>
        {error && <p className='user-form-error' style={{ marginTop: 12 }}>{error}</p>}
        <button
          type='submit'
          className='user-submit-btn'
          disabled={loading}
          style={{ marginTop: 20 }}
        >
          {loading ? 'Saving…' : 'Change Password'}
        </button>
      </form>
    </div>
  );
}
